import {Box, Button, HStack, Text} from '@chakra-ui/react';
import {useSearch} from './SearchContext';
import {CatalogProductsGrid} from '@/domains/catalogue/CatalogProductsGrid';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {SearchSidebar} from './SearchSidebar';
import {SEARCH_MIN_CHARS, routes} from 'ff-common';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {HEADER_HEIGHT} from '@/constants/layout';

const MAX_LIVE_RESULTS = 8;

/**
 * Contenu affiché à la place de <Outlet/> quand une recherche est active (md+).
 * - lg+ : sidebar catégories + opérations commerciales à gauche, cards produits à droite
 * - md : cards produits uniquement
 */
export const SearchResults: React.FC = () => {
  const {liveTerm, liveResults} = useSearch();
  const location = useLocation();
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {buildClientRoute} = useClientRoute();

  if (!liveTerm || liveTerm.length < SEARCH_MIN_CHARS) {
    return null;
  }

  const hits = liveResults?.hits.slice(0, MAX_LIVE_RESULTS) ?? [];

  // Navigue vers la page de résultats classique (/recherche?q=...)
  const handleVoirTous = () => {
    navigate(buildClientRoute(routes.client.recherche, {}, {q: liveTerm}));
  };

  return (
    <Box
      bg='white'
      minH='80vh'
    >
      <HStack
        alignItems='start'
        gap='0'
        w='100%'
      >
        {/* Sidebar catégories + opérations commerciales (lg+ uniquement) */}
        <Box
          display={{base: 'none', lg: 'block'}}
          w={{lg: '280px', xl: '320px'}}
          flexShrink={0}
          position='sticky'
          top={HEADER_HEIGHT.md}
          alignSelf='flex-start'
          maxH={`calc(100vh - ${HEADER_HEIGHT.md})`}
          overflowY='auto'
          px='20px'
          pt='20px'
          pb='20px'
        >
          <SearchSidebar />
        </Box>

        {/* Grille produits */}
        <Box
          flex='1'
          minW='0'
          bg='gray.75'
        >
          {/* Header : Produits + Voir tous les produits */}
          <HStack
            justify='space-between'
            px='20px'
            pt='30px'
          >
            <Text
              fontSize='md'
              fontWeight='700'
              color='blue.text'
            >
              {t('recherche.produits')}
            </Text>
            {liveResults && liveResults.count > MAX_LIVE_RESULTS && (
              <Button
                variant='link'
                fontSize='sm'
                color='blue.main'
                fontWeight='500'
                textDecoration='underline'
                onClick={handleVoirTous}
              >
                {t('recherche.voir_tous_les_produits')}
              </Button>
            )}
          </HStack>
          {hits.length > 0 ? (
            <CatalogProductsGrid
              hitResult={hits}
              location={location}
            />
          ) : liveResults ? (
            <Box
              p='40px'
              textAlign='center'
            >
              <Text fontWeight='500'>{t('catalogue.aucun_produit')}</Text>
            </Box>
          ) : null}
        </Box>
      </HStack>
    </Box>
  );
};
